// SPDX-License-Identifier: MIT
// Web data bridge — delegates to the existing Supabase project API verbatim.
// Nothing new ; routes the seam to today's code so the deployed web app is
// byte-for-byte unchanged.
import {
  listProjects,
  saveProject,
  saveProjectSeed,
  deleteProject,
  saveProjectVersion,
  listProjectVersions,
  switchToProjectVersion,
  saveDiscoveryAnswers,
  saveProjectBackend,
  clearProjectBackend,
  loadConversationHistory,
} from '../projects/api';
import type { DataBridge } from './data-types';
import { supabase } from '../auth/supabase';

// supabase-js FunctionsHttpError carries the Response in `.context`. Pull the
// status + structured `reason` out so callers (e.g. the card's capacity-vs-
// generic UX) can branch on "429"/"capacity" instead of a generic message.
async function describeFnError(error: { message?: string; context?: unknown }): Promise<string> {
  const ctx = error?.context as { status?: number; clone?: () => Response } | undefined;
  if (ctx && typeof ctx.status === 'number') {
    let detail = String(ctx.status);
    try {
      const body = await (ctx as unknown as Response).clone().json();
      if (body?.reason) detail += ` ${body.reason}`;
      else if (body?.error) detail += ` ${body.error}`;
    } catch { /* body not JSON — status alone is enough */ }
    return detail;
  }
  return error?.message ?? 'unknown error';
}

export const webDataBridge: DataBridge = {
  listProjects: () => listProjects(),
  saveProject: (result, idea, locale) => saveProject(result, idea, locale),
  saveProjectSeed: (idea, locale) => saveProjectSeed(idea, locale),
  deleteProject: (id) => deleteProject(id),
  saveProjectVersion: (projectId, result, kind, contextSnapshot) =>
    saveProjectVersion(projectId, result, kind, contextSnapshot),
  listProjectVersions: (projectId) => listProjectVersions(projectId),
  switchToProjectVersion: (projectId, versionNumber) => switchToProjectVersion(projectId, versionNumber),
  // No-op on web: the discover-attachments edge fn already persisted the snapshot
  // (summary/questions/attachments/run_at) server-side on the projects row. The seam
  // exists for the desktop bridge, which has no server to write it. Kept as a method
  // (not omitted) so the UI never branches on platform.
  saveDiscovery: () => Promise.resolve(),
  saveDiscoveryAnswers: (projectId, answers) => saveDiscoveryAnswers(projectId, answers),
  saveProjectBackend: (projectId, url, anonKey) => saveProjectBackend(projectId, url, anonKey),
  clearProjectBackend: (projectId) => clearProjectBackend(projectId),
  provisionBackend: async (projectId, opts) => {
    const body: Record<string, unknown> = { project_id: projectId };
    // The wizard supplies a short handle (subdomain) + the chosen model. Omit
    // them when absent so legacy callers keep the server-side title-slug default.
    if (opts?.handle) body.slug = opts.handle;
    if (opts?.model) body.model = opts.model;
    const { data, error } = await supabase.functions.invoke('provision-backend', {
      body,
    });
    if (error) {
      throw new Error(`[web] provision-backend failed: ${await describeFnError(error)}`);
    }
    if (!data?.backend_url || !data?.backend_anon_key) {
      throw new Error('[web] provision-backend failed: no creds returned');
    }
    return {
      backendUrl: data.backend_url as string,
      backendAnonKey: data.backend_anon_key as string,
      expiresAt: (data.expires_at as string | null) ?? null,
      alreadyProvisioned: Boolean(data.already_provisioned),
    };
  },
  deployFrontend: async (projectId, opts) => {
    const { data, error } = await supabase.functions.invoke('deploy-frontend', { body: { project_id: projectId, handle: opts?.handle, redeploy: opts?.redeploy } });
    if (error) throw new Error(`[web] deploy-frontend failed: ${await describeFnError(error)}`);
    if (!data?.deploy_url) throw new Error('[web] deploy-frontend failed: no deploy_url');
    return { deployUrl: data.deploy_url as string, deployId: (data.deploy_id as string | null) ?? null, status: (data.status as 'building' | 'live' | 'failed') ?? 'building' };
  },
  getDeployStatus: async (projectId) => {
    const { data, error } = await supabase.functions.invoke('deploy-frontend', { body: { project_id: projectId, status_only: true } });
    if (error) throw new Error(`[web] deploy-status failed: ${await describeFnError(error)}`);
    // null (never deployed / torn_down) passes through unchanged — do NOT default to
    // 'building', that would lie about a project that was never (or no longer) deployed.
    return { status: (data?.status as 'building' | 'live' | 'failed' | null) ?? null, deployUrl: (data?.deploy_url as string | null) ?? null };
  },
  loadConversationHistory: (projectId) => loadConversationHistory(projectId),
};
